import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../config';
import './AdminGestionPage.css';

const emptyPrestation = { titre: '', description: '', prix: '', active: true };
const emptyCapsule = { titre: '', description: '' };

function AdminGestionPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const [prestations, setPrestations] = useState([]);
  const [capsules, setCapsules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingPrestation, setEditingPrestation] = useState(null); // objet en cours d'édition, ou null
  const [prestationForm, setPrestationForm] = useState(emptyPrestation);

  const [editingCapsule, setEditingCapsule] = useState(null);
  const [capsuleForm, setCapsuleForm] = useState(emptyCapsule);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/prestations`).then(r => r.json()),
      fetch(`${API_URL}/capsules`).then(r => r.json())
    ])
      .then(([presData, capData]) => {
        setPrestations(presData);
        setCapsules(capData);
        setLoading(false);
      })
      .catch(() => {
        setError("Impossible de charger les données");
        setLoading(false);
      });
  };

  // ---------- PRESTATIONS ----------

  const startCreatePrestation = () => {
    setEditingPrestation('new');
    setPrestationForm(emptyPrestation);
  };

  const startEditPrestation = (p) => {
    setEditingPrestation(p.id);
    setPrestationForm({
      titre: p.titre || '',
      description: p.description || '',
      prix: p.prix ?? '',
      active: p.active
    });
  };

  const cancelPrestation = () => {
    setEditingPrestation(null);
    setPrestationForm(emptyPrestation);
  };

  const savePrestation = () => {
    const payload = {
      titre: prestationForm.titre,
      description: prestationForm.description,
      prix: prestationForm.prix === '' ? null : parseFloat(prestationForm.prix),
      active: prestationForm.active
    };

    const isNew = editingPrestation === 'new';
    const url = isNew ? `${API_URL}/prestations` : `${API_URL}/prestations/${editingPrestation}`;
    const method = isNew ? 'POST' : 'PUT';

    fetch(url, { method, headers: authHeaders, body: JSON.stringify(payload) })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        cancelPrestation();
        loadAll();
      })
      .catch(() => setError("Erreur lors de l'enregistrement de la prestation"));
  };

  const deletePrestation = (id) => {
    if (!window.confirm("Supprimer cette prestation ?")) return;
    fetch(`${API_URL}/prestations/${id}`, { method: 'DELETE', headers: authHeaders })
      .then(res => {
        if (!res.ok) throw new Error();
        loadAll();
      })
      .catch(() => setError("Erreur lors de la suppression"));
  };

  // ---------- CAPSULES ----------

  const startCreateCapsule = () => {
    setEditingCapsule('new');
    setCapsuleForm(emptyCapsule);
  };

  const startEditCapsule = (c) => {
    setEditingCapsule(c.id);
    setCapsuleForm({ titre: c.titre || '', description: c.description || '' });
  };

  const cancelCapsule = () => {
    setEditingCapsule(null);
    setCapsuleForm(emptyCapsule);
  };

  const saveCapsule = () => {
    const isNew = editingCapsule === 'new';
    const url = isNew ? `${API_URL}/capsules` : `${API_URL}/capsules/${editingCapsule}`;
    const method = isNew ? 'POST' : 'PUT';

    fetch(url, { method, headers: authHeaders, body: JSON.stringify(capsuleForm) })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        cancelCapsule();
        loadAll();
      })
      .catch(() => setError("Erreur lors de l'enregistrement de la capsule"));
  };

  const deleteCapsule = (id) => {
    if (!window.confirm("Supprimer cette capsule ?")) return;
    fetch(`${API_URL}/capsules/${id}`, { method: 'DELETE', headers: authHeaders })
      .then(res => {
        if (!res.ok) throw new Error();
        loadAll();
      })
      .catch(() => setError("Erreur lors de la suppression"));
  };

  if (loading) return <div className="gestion-page"><p>Chargement...</p></div>;

  return (
    <div className="gestion-page">
      <div className="gestion-header">
        <h1>Gestion du site</h1>
        <div className="dispo-header-links">
          <Link to="/admin/vitrine" className="back-link">Vitrine (Collections)</Link>
          <Link to="/admin/disponibilites" className="back-link">Disponibilités</Link>
          <Link to="/admin" className="back-link">Calendrier des réservations</Link>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* ---------- PRESTATIONS ---------- */}
      <section className="gestion-section">
        <div className="section-title-row">
          <h2>Prestations</h2>
          {editingPrestation === null && (
            <button className="add-button" onClick={startCreatePrestation}>+ Ajouter une prestation</button>
          )}
        </div>

        {editingPrestation !== null && (
          <div className="edit-card">
            <input
              type="text" placeholder="Titre"
              value={prestationForm.titre}
              onChange={e => setPrestationForm({ ...prestationForm, titre: e.target.value })}
            />
            <textarea
              placeholder="Description"
              value={prestationForm.description}
              onChange={e => setPrestationForm({ ...prestationForm, description: e.target.value })}
            />
            <div className="field-row">
              <input
                type="number" step="0.01" placeholder="Prix (€)"
                value={prestationForm.prix}
                onChange={e => setPrestationForm({ ...prestationForm, prix: e.target.value })}
              />
            </div>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={prestationForm.active}
                onChange={e => setPrestationForm({ ...prestationForm, active: e.target.checked })}
              />
              Visible sur le site
            </label>
            <div className="edit-actions">
              <button className="save-button" onClick={savePrestation}>Enregistrer</button>
              <button className="cancel-button" onClick={cancelPrestation}>Annuler</button>
            </div>
          </div>
        )}

        <div className="items-list">
          {prestations.map(p => (
            <div className="item-card" key={p.id}>
              <div className="item-info">
                <strong>{p.titre}</strong>
                <span className="item-sub">{p.prix != null ? `${p.prix} €` : ''} {!p.active && '· masquée'}</span>
                {p.description && <p className="item-desc">{p.description}</p>}
              </div>
              <div className="item-actions">
                <button onClick={() => startEditPrestation(p)}>Modifier</button>
                <button className="delete-button" onClick={() => deletePrestation(p.id)}>Supprimer</button>
              </div>
            </div>
          ))}
          {prestations.length === 0 && <p className="empty-hint">Aucune prestation pour l'instant.</p>}
        </div>
      </section>

      {/* ---------- CAPSULES ---------- */}
      <section className="gestion-section">
        <div className="section-title-row">
          <h2>Capsules</h2>
          {editingCapsule === null && (
            <button className="add-button" onClick={startCreateCapsule}>+ Ajouter une capsule</button>
          )}
        </div>

        {editingCapsule !== null && (
          <div className="edit-card">
            <input
              type="text" placeholder="Titre"
              value={capsuleForm.titre}
              onChange={e => setCapsuleForm({ ...capsuleForm, titre: e.target.value })}
            />
            <textarea
              placeholder="Description"
              value={capsuleForm.description}
              onChange={e => setCapsuleForm({ ...capsuleForm, description: e.target.value })}
            />
            <div className="edit-actions">
              <button className="save-button" onClick={saveCapsule}>Enregistrer</button>
              <button className="cancel-button" onClick={cancelCapsule}>Annuler</button>
            </div>
          </div>
        )}

        <div className="items-list">
          {capsules.map(c => (
            <div className="item-card" key={c.id}>
              <div className="item-info">
                <strong>{c.titre}</strong>
                {c.description && <p className="item-desc">{c.description}</p>}
              </div>
              <div className="item-actions">
                <button onClick={() => startEditCapsule(c)}>Modifier</button>
                <button className="delete-button" onClick={() => deleteCapsule(c.id)}>Supprimer</button>
              </div>
            </div>
          ))}
          {capsules.length === 0 && <p className="empty-hint">Aucune capsule pour l'instant.</p>}
        </div>
      </section>
    </div>
  );
}

export default AdminGestionPage;
